<template>
  <div class="container text-white animate__animated animate__fadeIn">
    <div class="mb-4">
      <router-link to="/portfolio" class="btn btn-sm btn-outline-primary">
        <i class="fas fa-arrow-left me-1"></i> {{ $t('project.back') }}
      </router-link>
    </div>

    <div v-if="loading" class="text-center my-5">
      <div class="spinner-border text-primary" role="status"></div>
    </div>

    <div v-else-if="project" class="card project-box border-primary p-4 shadow-lg">
      <h2 class="text-primary fw-bold mb-4 text-center">{{ project.title }}</h2>
      
      <div v-if="project.images.length" class="carousel mb-4 text-center">
        <div class="bg-dark image-container" @click="openModal(project.images[currentImage])">
          <TransitionGroup name="fade-image" tag="div" class="carousel-images-wrapper">
            <img
              v-for="(img, index) in project.images"
              :key="img.url"
              v-show="index === currentImage"
              :src="getImageUrl(img.url)"
              :alt="img.alt"
              class="img-fluid w-100 rounded shadow border border-primary project-img-item"
              :style="{
                'object-fit': 'cover', /* Am schimbat fill în cover pentru o redare mai bună */
                'max-height': '400px',
                'width': '100%',
                'cursor': 'zoom-in',
              }"
            />
          </TransitionGroup>
        </div>
        <div class="d-flex justify-content-between mt-2">
          <button class="btn btn-sm btn-outline-primary" @click="prevImage">
            <i class="fas fa-chevron-left"></i>
          </button>
          <button class="btn btn-sm btn-outline-primary" @click="nextImage">
            <i class="fas fa-chevron-right"></i>
          </button>
        </div>
        <div class="d-flex justify-content-center gap-2 mt-3">
          <span
            v-for="(img, index) in project.images"
            :key="img.url"
            class="indicator"
            :class="{ active: index === currentImage }"
            @click="currentImage = index"
          ></span>
        </div>
      </div>

      <span class="text-white">{{  project.date_realizare }}</span>
      <p class="text-light fs-5 mb-3">{{ project.description }}</p>
      <p v-if="project.descriptionl" class="text-white mb-4" style="line-height: 1.6; white-space: pre-line">
        {{ project.descriptionl }}
      </p>


      <h5 class="text-primary mt-3"><i class="fas fa-microchip me-2"></i>{{ $t('project.techUsed') }}</h5>
      <div class="d-flex flex-wrap gap-2 mb-4 mt-2">
        <span
          v-for="tech in project.tech_stack"
          :key="tech.slug"
          class="badge bg-dark d-flex align-items-center gap-1 border border-primary px-2 py-1"
        >
          <i :class="`fa ${tech.icon}`"></i> {{ tech.name }}
        </span>
      </div>

      <div class="d-flex flex-wrap gap-3 mt-3">
        <a v-if="project.live_url" :href="project.live_url" class="btn btn-outline-primary" target="_blank">
          <i class="fas fa-globe me-1"></i> {{ $t('project.live') }}
        </a>
        <a v-if="project.source_code_url" :href="project.source_code_url" class="btn btn-outline-primary" target="_blank">
          <i class="fas fa-code me-1"></i> {{ $t('project.sourceCode') }}
        </a>
        <a v-for="dl in project.downloads" :key="dl.url" :href="dl.url" class="btn btn-outline-primary" download>
          <i class="fas fa-download me-1"></i> {{ dl.label }}
        </a>
      </div>
    </div>

    <div v-else class="text-danger text-center mt-5 fs-5">
      {{ $t('project.notFound') }}
    </div>

    <teleport to="body">
      <transition name="fade">
        <div v-if="isModalOpen" class="modal-overlay" @click.self="closeModal">
          <div class="modal-content">
            <button class="close-btn" @click="closeModal">
              <i class="fas fa-times"></i>
            </button>
            <img
              :src="getImageUrl(selectedImage.url)"
              :alt="selectedImage.alt"
              class="modal-img"
            />
          </div>
        </div>
      </transition>
    </teleport>

  </div>
</template>
<script setup>
import { onMounted, onUnmounted, ref, watch } from 'vue' // Adaugă 'watch'
import { useRoute } from 'vue-router'
import axios from 'axios'

const route = useRoute()
const slug = route.params.slug
const project = ref(null)
const loading = ref(true)

const currentImage = ref(0)
const isModalOpen = ref(false)
const selectedImage = ref({ url: '', alt: '' })
const interval = ref(null) // Declară interval aici pentru a fi accesibil peste tot

const getImageUrl = (url) => {
  if (!url) return ''
  if (url.startsWith('http')) return url
  return `https://api.rusin.ro${url}`
}

const openModal = (image) => {
  selectedImage.value = image
  isModalOpen.value = true
  stopAutoplay() // Oprește slideshow-ul când modalul e deschis
  // Adaugă o clasă la body pentru a preveni scroll-ul sub modal
  document.body.style.overflow = 'hidden';
}

const closeModal = () => {
  isModalOpen.value = false
  // Reia slideshow-ul dacă există imagini și dacă nu e deja în autoplay
  if (project.value?.images?.length > 1) {
    startAutoplay()
  }
  // Scoate clasa de pe body
  document.body.style.overflow = '';
}

const nextImage = () => {
  if (project.value && project.value.images.length > 0) {
    currentImage.value = (currentImage.value + 1) % project.value.images.length
  }
}

const prevImage = () => {
  if (project.value && project.value.images.length > 0) {
    currentImage.value =
      (currentImage.value - 1 + project.value.images.length) % project.value.images.length
  }
}

const startAutoplay = () => {
  // Pornește slideshow-ul doar dacă sunt mai mult de o imagine și nu e deja pornit
  if (project.value && project.value.images.length > 1 && !interval.value) {
    interval.value = setInterval(() => {
      nextImage()
    }, 5000)
  }
}

const stopAutoplay = () => {
  if (interval.value) {
    clearInterval(interval.value)
    interval.value = null // Setează la null pentru a indica că e oprit
  }
}

// Watcher pentru a reseta autoplay-ul când currentImage se schimbă manual
watch(currentImage, () => {
  resetAutoplay()
})

const resetAutoplay = () => {
  stopAutoplay()
  startAutoplay()
}

onMounted(async () => {
  try {
    const res = await axios.get(`https://api.rusin.ro/portofolio/${slug}/`)
    project.value = res.data

    // 🔄 Preîncărcare imagini
    if (project.value?.images?.length > 0) {
      project.value.images.forEach(img => {
        const preload = new Image()
        preload.src = getImageUrl(img.url)
      })
    }
    // Aici pornim autoplay-ul o singură dată la încărcarea proiectului
    startAutoplay()
  } catch (e) {
    console.error("❌ Nu s-a putut încărca proiectul:", e)
  } finally {
    loading.value = false
  }
})

onUnmounted(() => {
  stopAutoplay()
  // Asigură-te că scroll-ul e reactivat când componenta e demontată
  document.body.style.overflow = '';
});
</script>
<style scoped>
.project-box {
  background-color: #111;
  border: 1px solid #00ffcc44;
  transition: all 0.2s ease;
}

input.custom-input,
textarea.custom-input {
  color: #fff !important;
}

.custom-input {
  background-color: #222;
  border: 1px solid #00ffcc;
  color: white;
  
  font-family: 'Fira Code', monospace;
  transition: all 0.3s ease;
  box-shadow: 0 0 5px transparent;
}
.custom-input::placeholder {
  color: #00ffccaa;
}
.custom-input:input{
  color: white;
}
.custom-input:focus {
  border-color: #00ffcc;
  box-shadow: 0 0 8px #00ffccaa;
  background-color: #111;
}
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.6s ease;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}
.fade-image-enter-active, .fade-image-leave-active {
  transition: opacity 0.6s ease;
}
.fade-image-enter-from, .fade-image-leave-to {
  opacity: 0;
}

.indicator {
  width: 10px;
  height: 10px;
  border: 1px solid #00ffcc;
  border-radius: 50%;
  background-color: transparent;
  cursor: pointer;
  transition: all 0.3s ease;
}
.indicator.active {
  background-color: #00ffcc;
  box-shadow: 0 0 5px #00ffcc;
}

.indicator {
  width: 10px;
  height: 10px;
  border: 1px solid #00ffcc;
  border-radius: 50%;
  background-color: transparent;
  cursor: pointer;
  transition: all 0.3s ease;
}
.indicator.active {
  background-color: #00ffcc;
  box-shadow: 0 0 5px #00ffcc;
}

.text-primary {
  color: #00ffcc !important;
}
.project-img {
  width: 100%;
  height: 100%;
  object-fit: cover fill; /* se întinde și taie marginile dacă trebuie */
  margin: 0;
  padding: 0;
  display: block;
}


.border-primary {
  border-color: #00ffcc !important;
}
.card {
  background-color: #151515;
  border-radius: 10px;
  transition: transform 0.3s ease;
}

/* Stiluri pentru a preveni re-randarea imaginii cu efect de fade */
.image-container {
  position: relative;
  height: 400px; /* Setează o înălțime fixă pentru containerul caruselului */
  overflow: hidden;
  display: flex; /* Folosim flex pentru a centra imaginile în TransitionGroup */
  justify-content: center;
  align-items: center;
}

/* Wrapper-ul pentru TransitionGroup */
.carousel-images-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
}

.project-img-item {
  position: absolute; /* Toate imaginile se suprapun */
  top: 0;
  left: 0;
  width: 100%;
  height: 100%; /* Important: ocupă toată înălțimea containerului image-container */
  object-fit: cover; /* Am schimbat fill în cover */
  border-radius: 5px; /* Păstrează border-radius-ul */
}


/* Stiluri pentru tranziția Fade a imaginilor din carusel (folosite de TransitionGroup) */
.fade-image-enter-active,
.fade-image-leave-active {
  transition: opacity 0.6s ease;
}
.fade-image-enter-from,
.fade-image-leave-to {
  opacity: 0;
}
.fade-image-enter-to,
.fade-image-leave-from {
  opacity: 1;
}

/* Modal Overlay - ACUM ACOPERĂ TOT VIEWPORT-UL */
.modal-overlay {
  position: fixed; /* Folosim fixed pentru a acoperi tot viewport-ul */
  top: 0;
  left: 0;
  width: 100vw; /* 100% din lățimea viewport-ului */
  height: 100vh; /* 100% din înălțimea viewport-ului */
  background-color: rgba(0, 0, 0, 0.9);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999; /* Asigură că e deasupra tuturor (valoarea foarte mare) */
  backdrop-filter: blur(5px);
}

.modal-content {
  position: relative;
  max-width: 95vw; /* Limită la 95% din lățimea viewport-ului */
  max-height: 95vh; /* Limită la 95% din înălțimea viewport-ului */
  padding: 0;
}

.modal-img {
  display: block;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: 5px;
  box-shadow: 0 0 20px rgba(0, 255, 204, 0.5);
}

.close-btn {
  position: absolute;
  top: 15px; /* Ajustează poziția pentru vizibilitate mai bună */
  right: 15px; /* Ajustează poziția pentru vizibilitate mai bună */
  background: rgba(0, 0, 0, 0.7); /* Background mai închis */
  color: #00ffcc;
  border: 2px solid #00ffcc;
  border-radius: 50%;
  width: 45px; /* Mărește butonul */
  height: 45px; /* Mărește butonul */
  font-size: 1.8rem; /* Mărește iconița */
  display: flex; /* Centrare iconiță */
  justify-content: center;
  align-items: center;
  cursor: pointer;
  z-index: 10000; /* Asigură că e deasupra imaginii din modal */
  transition: all 0.3s ease;
}

.close-btn:hover {
  background: #00ffcc;
  color: #111;
  transform: rotate(90deg); /* Adaugă o mică animație la hover */
}

/* Stiluri pentru tranziția fade a modalului */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

/* ... Restul stilurilor rămân la fel ... */
.card-title {
  font-weight: bold;
  color: #00ffcc;
  font-family: 'Fira Code', monospace;
}
.card-text {
  font-size: 0.9rem;
  color: #ccc;
}
.btn-outline-primary {
  position: relative;
  color: #00ffcc !important;
  border: 2px solid #00ffcc;
  background-color: transparent;
  transition: all 0.3s ease;
  font-weight: 600;
  letter-spacing: 0.5px;
  box-shadow: 0 0 8px transparent;
}

.btn-outline-primary:hover {
  color: #fff !important;
  background-color: #00ffcc !important;
  border-color: #00ffcc;
  transform: translateY(-2px);
}
.btn-outline-primary.active {
  color: #111 !important;
  background-color: #00ffcc !important;
  border-color: #00ffcc;
  transform: translateY(-2px);
}
.btn-outline-primary:disabled {
  color: #fff !important;
  border-color: #00a584;
  transform: translateY(-2px);
}
</style>
